/**
 * Light Cycles rules. Pure and deterministic: the server advances every round with
 * `stepRound`, the client predicts its own cycle with the same function and the same turn
 * queue helpers, so what the player sees at a cell boundary is what the server will decide.
 */
import { seededRandom } from '../random'
import {
  ARENA_CELLS,
  ARENA_H,
  ARENA_W,
  type CycleSnapshot,
  cellIndex,
  DX,
  DY,
  type Heading,
  inBounds,
  isPerpendicular,
  MAX_QUEUED_TURNS,
  type RoundState,
} from './types'

/** Cycles start this far from the arena edge, on a ring, evenly spaced. */
const START_INSET = 6

/**
 * Start positions for `ids.length` cycles: evenly spaced around an inset rectangle with a seeded
 * rotation, each facing the arena's interior. Two cycles end up on opposite points of the ring.
 */
export function startCycles(ids: readonly string[], seed: number): CycleSnapshot[] {
  const left = START_INSET
  const top = START_INSET
  const right = ARENA_W - 1 - START_INSET
  const bottom = ARENA_H - 1 - START_INSET
  const w = right - left
  const h = bottom - top
  const perimeter = 2 * (w + h)
  const offset = Math.floor(seededRandom(seed)() * perimeter)
  const n = ids.length
  return ids.map((id, k) => {
    const d = (offset + Math.floor((k * perimeter) / n)) % perimeter
    let x: number
    let y: number
    let heading: Heading
    if (d < w) {
      x = left + d
      y = top
      heading = 2
    } else if (d < w + h) {
      x = right
      y = top + (d - w)
      heading = 3
    } else if (d < 2 * w + h) {
      x = right - (d - w - h)
      y = bottom
      heading = 0
    } else {
      x = left
      y = bottom - (d - 2 * w - h)
      heading = 1
    }
    return { id, x, y, heading, alive: true, diedAt: null, path: [x, y] }
  })
}

/** Every cell a path covers, endpoints included. */
function forEachPathCell(path: readonly number[], visit: (idx: number) => void) {
  if (path.length < 2) return
  let x = path[0]!
  let y = path[1]!
  visit(cellIndex(x, y))
  for (let i = 2; i < path.length; i += 2) {
    const tx = path[i]!
    const ty = path[i + 1]!
    const sx = Math.sign(tx - x)
    const sy = Math.sign(ty - y)
    while (x !== tx || y !== ty) {
      x += sx
      y += sy
      visit(cellIndex(x, y))
    }
  }
}

/** Rebuilds the occupancy grid from the cycles' paths (a client does this after a snapshot). */
export function cellsFromCycles(cycles: readonly CycleSnapshot[]): Uint8Array {
  const cells = new Uint8Array(ARENA_CELLS)
  cycles.forEach((c, i) => {
    forEachPathCell(c.path, (idx) => {
      cells[idx] = i + 1
    })
  })
  return cells
}

export function roundFromSnapshot(
  no: number,
  cycles: readonly CycleSnapshot[],
  tick: number,
): RoundState {
  return {
    no,
    tick,
    cycles: cycles.map((c) => ({ ...c, path: [...c.path] })),
    cells: cellsFromCycles(cycles),
  }
}

/** Sideways turns only; anything else leaves the cycle unchanged. */
export function applyTurn(cycle: CycleSnapshot, h: Heading): CycleSnapshot {
  if (!cycle.alive || !isPerpendicular(cycle.heading, h)) return cycle
  return { ...cycle, heading: h }
}

/** The path with its head moved to (x, y): the last segment grows, or a new one starts after a turn. */
export function extendPath(p: readonly number[], x: number, y: number): number[] {
  const n = p.length
  const path = [...p]
  const collinear =
    n >= 4 &&
    ((p[n - 4] === p[n - 2] && p[n - 2] === x) || (p[n - 3] === p[n - 1] && p[n - 1] === y))
  if (collinear) {
    path[n - 2] = x
    path[n - 1] = y
  } else {
    path.push(x, y)
  }
  return path
}

/** Moves the head one cell. */
function advance(cycle: CycleSnapshot, x: number, y: number): CycleSnapshot {
  return { ...cycle, x, y, path: extendPath(cycle.path, x, y) }
}

export interface CyclesStep {
  round: RoundState
  /** Ids that crashed on this tick, in cycle order. */
  deaths: string[]
}

/**
 * One tick: turns are applied, every living cycle moves one cell at once, and crashes are
 * judged against the grid *before* anyone moved (walls, any trail, any head, and two cycles
 * racing for the same empty cell all count). Never mutates its input.
 */
export function stepRound(round: RoundState, intents: ReadonlyMap<string, Heading>): CyclesStep {
  const n = round.cycles.length
  const turned = round.cycles.map((c) => {
    const h = intents.get(c.id)
    return h === undefined ? c : applyTurn(c, h)
  })
  const targets = new Int32Array(n).fill(-1)
  const dies = new Uint8Array(n)
  const claims = new Map<number, number>()
  for (let i = 0; i < n; i++) {
    const c = turned[i]!
    if (!c.alive) continue
    const tx = c.x + DX[c.heading]!
    const ty = c.y + DY[c.heading]!
    if (!inBounds(tx, ty)) {
      dies[i] = 1
      continue
    }
    const idx = cellIndex(tx, ty)
    if (round.cells[idx] !== 0) {
      dies[i] = 1
      continue
    }
    targets[i] = idx
    claims.set(idx, (claims.get(idx) ?? 0) + 1)
  }
  const cells = new Uint8Array(round.cells)
  const tick = round.tick + 1
  const deaths: string[] = []
  const cycles = turned.map((c, i) => {
    if (!c.alive) return c
    const idx = targets[i]!
    if (dies[i] || (claims.get(idx) ?? 0) > 1) {
      deaths.push(c.id)
      return { ...c, alive: false, diedAt: tick }
    }
    cells[idx] = i + 1
    return advance(c, c.x + DX[c.heading]!, c.y + DY[c.heading]!)
  })
  return { round: { no: round.no, tick, cycles, cells }, deaths }
}

export function aliveCount(round: RoundState): number {
  let n = 0
  for (const c of round.cycles) if (c.alive) n++
  return n
}

/** A round ends once at most one cycle is left (or nobody, after a shared crash). */
export function roundOver(round: RoundState): boolean {
  return aliveCount(round) <= 1
}

/** 1-based finishing places: survivors first, then by crash tick (later is better); ties share. */
export function placementsFor(cycles: readonly CycleSnapshot[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const c of cycles) {
    let ahead = 0
    for (const o of cycles) {
      if (o === c) continue
      const outlived = c.diedAt !== null && (o.diedAt === null || o.diedAt > c.diedAt)
      if (outlived) ahead++
    }
    out[c.id] = ahead + 1
  }
  return out
}

/** Adds a turn to a player's queue: repeats are dropped, a full queue replaces its last entry. */
export function enqueueTurn(
  queue: readonly Heading[],
  h: Heading,
  max = MAX_QUEUED_TURNS,
): Heading[] {
  if (queue[queue.length - 1] === h) return [...queue]
  if (queue.length >= max) return [...queue.slice(0, max - 1), h]
  return [...queue, h]
}

/** Takes the first sideways turn off the queue, discarding anything that isn't one. */
export function popApplicable(
  queue: readonly Heading[],
  heading: Heading,
): { turn: Heading | null; queue: Heading[] } {
  for (let i = 0; i < queue.length; i++) {
    const h = queue[i]!
    if (isPerpendicular(h, heading)) return { turn: h, queue: queue.slice(i + 1) }
  }
  return { turn: null, queue: [] }
}
